import { useState } from "react";
import { Loader2, Send } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useCurrentUser } from "@/hooks/useCurrentUser";
import { useSubmitApplication } from "@/hooks/useApplication";
import type { ApplicationRecord } from "@/hooks/useChama";
import type { JoinRequest } from "@/lib/concord/types";

const CONFIRMATIONS = [
  { id: "kenya", label: "I'm based in Kenya" },
  { id: "woman", label: "I'm a woman" },
  { id: "oss", label: "I build open-source Bitcoin and/or Nostr software" },
] as const;

export function ApplicationForm({
  onSubmitted,
  initial,
}: {
  onSubmitted: (record: ApplicationRecord) => void;
  initial?: JoinRequest | null;
}) {
  const { user, metadata } = useCurrentUser();
  const [name, setName] = useState(initial?.name ?? "");
  const [about, setAbout] = useState(initial?.about ?? "");
  const [why, setWhy] = useState(initial?.why ?? "");
  const [links, setLinks] = useState(initial?.links ?? "");
  const [checked, setChecked] = useState<string[]>(initial?.confirmations ?? []);

  const submit = useSubmitApplication((request) =>
    onSubmitted({ at: Date.now(), request }),
  );

  const allChecked = CONFIRMATIONS.every((c) => checked.includes(c.id));
  const valid = about.trim().length >= 20 && why.trim().length >= 20 && allChecked;

  const toggle = (id: string) =>
    setChecked((prev) =>
      prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id],
    );

  return (
    <Card className="shadow-md border-primary/20">
      <CardHeader>
        <CardTitle className="font-display text-2xl">Request to join</CardTitle>
        <CardDescription>
          Signed with your Nostr key as{" "}
          <span className="font-medium text-foreground">
            {metadata?.name ?? metadata?.display_name ?? `${user?.pubkey.slice(0, 12)}…`}
          </span>{" "}
          and delivered privately to the moderator. She reviews every
          application herself.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            if (!valid || submit.isPending) return;
            submit.mutate({
              name: name.trim(),
              about: about.trim(),
              why: why.trim(),
              links: links.trim(),
              confirmations: checked,
            });
          }}
        >
          <div className="space-y-2">
            <Label htmlFor="apply-name">What should we call you? (optional)</Label>
            <Input
              id="apply-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="A name or handle"
              maxLength={80}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="apply-about">
              Who are you? <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="apply-about"
              value={about}
              onChange={(e) => setAbout(e.target.value)}
              placeholder="What you build, the projects you work on, how long you've been in Bitcoin or Nostr…"
              rows={4}
              maxLength={4000}
              required
            />
            <p className="text-xs text-muted-foreground">
              At least 20 characters. Links to your work help.
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="apply-why">
              Why do you want to join the chama? <span className="text-destructive">*</span>
            </Label>
            <Textarea
              id="apply-why"
              value={why}
              onChange={(e) => setWhy(e.target.value)}
              placeholder="What you're looking for, what you'd like to give back…"
              rows={4}
              maxLength={4000}
              required
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="apply-links">Links (optional)</Label>
            <Input
              id="apply-links"
              value={links}
              onChange={(e) => setLinks(e.target.value)}
              placeholder="GitHub, Nostr profile, portfolio…"
              maxLength={1000}
            />
          </div>

          <fieldset className="space-y-3">
            <legend className="text-sm font-medium">Please confirm</legend>
            {CONFIRMATIONS.map((c) => (
              <div key={c.id} className="flex items-center gap-3">
                <Checkbox
                  id={`confirm-${c.id}`}
                  checked={checked.includes(c.id)}
                  onCheckedChange={() => toggle(c.id)}
                />
                <Label htmlFor={`confirm-${c.id}`} className="font-normal cursor-pointer">
                  {c.label}
                </Label>
              </div>
            ))}
          </fieldset>

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={!valid || submit.isPending}
          >
            {submit.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Send className="size-4" />
            )}
            Submit application
          </Button>
          <p className="text-xs text-muted-foreground text-center">
            Your answers travel end-to-end encrypted, visible only to the
            moderator.
          </p>
        </form>
      </CardContent>
    </Card>
  );
}
